import React, { forwardRef, useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { trackComponentRenderError, trackOperation } from 'events/ops/opsEvent';
import type { UserOperation } from 'events/ops/opsEvent';
import { getType } from 'history/historyFactory';
import { logErrorAndLogToServer } from 'middleware/logger';

interface Props {
  boundaryTag: string;
  children: React.ReactNode;
  operationName?: UserOperation;
}

const MartyErrorBoundary = ({ boundaryTag, children, operationName }: Props) => {
  // useCallback w/ react-hooks so we don't pass a brand new function every time this re-renders
  const FallbackComponent = useCallback(
    () => (
      <div
        data-test-id="errorBoundaryFallback"
        style={{ display: 'none' }}
        data-component-boundary-error={`Error while rendering the component ${boundaryTag ? `([${boundaryTag}])` : ''}`}
      />
    ),
    [boundaryTag]
  );

  const onError = useCallback(
    (error: Error) => {
      const tagPrefix = boundaryTag ? `[${boundaryTag}] ` : '';
      const operationFailurePrefix = operationName ? `${operationName} failure ` : '';
      const errorMessage = `An error has occurred while rendering component contained with MartyErrorBoundary: ${error.toString()}`;
      logErrorAndLogToServer(`${tagPrefix}${operationFailurePrefix}${errorMessage}`);

      const pageType = getType(window.location.pathname) || 'unknown';
      trackComponentRenderError(pageType, boundaryTag);

      if (operationName) {
        trackOperation(operationName, 'failure');
      }
    },
    [boundaryTag, operationName]
  );

  return (
    <ErrorBoundary FallbackComponent={FallbackComponent} onError={onError}>
      {children}
    </ErrorBoundary>
  );
};

export const withErrorBoundary = <Props extends object>(
  boundaryTag: string,
  Component: React.ComponentType<Props>,
  operationName?: UserOperation
) => {
  const WrappedComponent = forwardRef<React.ReactNode, Props>((props, ref) => (
    <MartyErrorBoundary boundaryTag={boundaryTag} operationName={operationName}>
      {/* @ts-expect-error TODO fix this */}
      <Component ref={ref} {...props} />
    </MartyErrorBoundary>
  ));
  WrappedComponent.displayName = `${getDisplayName(Component)}ErrorBoundary`;
  return WrappedComponent;
};
// helper for HOC
function getDisplayName(WrappedComponent: React.ComponentType<any>) {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component';
}

export default MartyErrorBoundary;
